import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

/** Matches the Supabase project default; stated so the child is not guessing. */
const MIN_PASSWORD = 8;

/**
 * FR-01 — set a new password after following the emailed link.
 *
 * Supabase turns the link into a recovery session (detectSessionInUrl is on),
 * so by the time this page mounts the user is authenticated well enough to
 * change their own password and nothing else.
 */
export default function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [ready, setReady] = useState(false);
  const [linkValid, setLinkValid] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let mounted = true;
    // The recovery session may land a tick after mount, so check the session
    // rather than assuming the link already applied.
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) {
        return;
      }
      setLinkValid(Boolean(data.session));
      setReady(true);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD;
  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit = password.length >= MIN_PASSWORD && password === confirm && !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err: any) {
      setError(err.message || 'Gagal mengganti password.');
    } finally {
      setLoading(false);
    }
  };

  if (!ready) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="text-xl text-gray-600">Memeriksa tautan...</div>
      </div>
    );
  }

  if (!linkValid) {
    return (
      <div className="screen min-h-[100dvh] h-full">
        <div className="screen-card text-center">
          <div className="section-title">Tautan tidak berlaku</div>
          <div className="subtitle">
            Tautan reset password sudah kedaluwarsa atau pernah dipakai.
            Silakan minta tautan baru.
          </div>
          <div className="next-actions" style={{ marginTop: 16, justifyContent: 'center' }}>
            <button
              type="button"
              className="btn-primary"
              onClick={() => navigate('/forgot-password')}
            >
              Minta tautan baru
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="screen min-h-[100dvh] h-full">
        <div className="screen-card text-center" role="status">
          <div className="section-title">Password berhasil diganti</div>
          <div className="subtitle">Sebentar lagi kamu diarahkan ke halaman masuk.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="screen min-h-[100dvh] h-full">
      <div className="screen-card">
        <div className="section-title">Password baru</div>
        <div className="subtitle">Buat password baru untuk akunmu.</div>
        <form onSubmit={handleSubmit}>
          <div className="field" style={{ marginTop: 12 }}>
            <label htmlFor="new-password">Password baru</label>
            <input
              id="new-password"
              type="password"
              required
              minLength={MIN_PASSWORD}
              value={password}
              placeholder={`Minimal ${MIN_PASSWORD} karakter`}
              onChange={e => setPassword(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="confirm-password">Ulangi password</label>
            <input
              id="confirm-password"
              type="password"
              required
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
            />
          </div>

          {tooShort ? (
            <div className="target-band-error">
              Password minimal {MIN_PASSWORD} karakter.
            </div>
          ) : null}
          {mismatch ? (
            <div className="target-band-error">Password belum sama.</div>
          ) : null}
          {error ? <div className="target-band-error">{error}</div> : null}

          <div className="target-actions" style={{ marginTop: 14 }}>
            <button type="submit" className="btn-primary" disabled={!canSubmit}>
              {loading ? 'Menyimpan...' : 'Simpan password baru'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
